import type {
  AppErrorFields,
  ErrorCategory,
  ErrorPresentation,
  ErrorSeverity,
} from './types';
import { ERROR_CODES, USER_MESSAGE_KEYS } from './errorCodes';

const DEFAULT_PRESENTATION: Record<ErrorCategory, ErrorPresentation> = {
  validation: 'inline',
  auth: 'inline',
  permission: 'toast',
  not_found: 'toast',
  conflict: 'toast',
  offline: 'toast',
  unavailable: 'toast',
  rate_limit: 'toast',
  config: 'toast',
  external: 'toast',
  internal: 'toast',
};

const DEFAULT_SEVERITY: Record<ErrorCategory, ErrorSeverity> = {
  validation: 'warn',
  auth: 'warn',
  permission: 'warn',
  not_found: 'info',
  conflict: 'warn',
  offline: 'info',
  unavailable: 'warn',
  rate_limit: 'warn',
  config: 'error',
  external: 'warn',
  internal: 'error',
};

/**
 * Application error with a stable code for logs and a separate i18n key for users.
 * Never put stack traces or SDK text in userMessageKey.
 */
export class AppError extends Error {
  readonly code: string;
  readonly category: ErrorCategory;
  readonly userMessageKey: string;
  readonly hintKey?: string;
  readonly severity: ErrorSeverity;
  readonly retryable: boolean;
  readonly presentation: ErrorPresentation;
  readonly details: Record<string, unknown>;
  readonly traceId?: string;
  readonly spanId?: string;
  readonly cause?: unknown;

  constructor(fields: AppErrorFields, developerMessage?: string) {
    super(developerMessage ?? fields.code);
    this.name = 'AppError';
    this.code = fields.code;
    this.category = fields.category;
    this.userMessageKey = fields.userMessageKey;
    this.hintKey = fields.hintKey;
    this.severity = fields.severity ?? DEFAULT_SEVERITY[fields.category];
    this.retryable = fields.retryable ?? false;
    this.presentation = fields.presentation ?? DEFAULT_PRESENTATION[fields.category];
    this.details = fields.details ?? {};
    this.traceId = fields.traceId;
    this.spanId = fields.spanId;
  }

  static validation(userMessageKey: string, details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.VALIDATION,
      category: 'validation',
      userMessageKey,
      details,
      retryable: false,
    });
  }

  static fromValidationKey(key: string): AppError {
    return AppError.validation(key);
  }

  static permissionDenied(details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.PERMISSION_DENIED,
      category: 'permission',
      userMessageKey: USER_MESSAGE_KEYS.PERMISSION_DENIED,
      hintKey: USER_MESSAGE_KEYS.PERMISSION_DENIED_HINT,
      details,
    });
  }

  static offline(details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.OFFLINE,
      category: 'offline',
      userMessageKey: USER_MESSAGE_KEYS.OFFLINE,
      hintKey: USER_MESSAGE_KEYS.OFFLINE_HINT,
      retryable: true,
      severity: 'info',
      details,
    });
  }

  static serverUnreachable(details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.SERVER_UNREACHABLE,
      category: 'offline',
      userMessageKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE,
      hintKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE_HINT,
      retryable: true,
      severity: 'info',
      details,
    });
  }

  static unavailable(details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.NETWORK_UNAVAILABLE,
      category: 'unavailable',
      userMessageKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE,
      hintKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE_HINT,
      retryable: true,
      details,
    });
  }

  static internal(cause?: unknown, details?: Record<string, unknown>): AppError {
    const causeMessage = cause instanceof Error
      ? cause.message
      : typeof cause === 'string'
        ? cause
        : String(cause ?? '');
    return new AppError({
      code: ERROR_CODES.INTERNAL,
      category: 'internal',
      userMessageKey: USER_MESSAGE_KEYS.GENERIC,
      hintKey: USER_MESSAGE_KEYS.GENERIC_HINT,
      details: { ...details, causeMessage },
    }, causeMessage || ERROR_CODES.INTERNAL);
  }

  static configSaveFailed(details?: Record<string, unknown>): AppError {
    return new AppError({
      code: ERROR_CODES.CONFIG_SAVE_FAILED,
      category: 'config',
      userMessageKey: USER_MESSAGE_KEYS.CONFIG_SAVE_FAILED,
      hintKey: USER_MESSAGE_KEYS.CONFIG_SAVE_FAILED_HINT,
      details,
    });
  }

  isValidation(): boolean {
    return this.category === 'validation';
  }

  shouldQueueOffline(): boolean {
    return this.retryable && (this.category === 'offline' || this.category === 'unavailable');
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}
